import { FunctionComponent, ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppLogo from "@/assets/images/favicon.png";
import { ROUTE_CONFIG } from "@/routes/config";

import { skipsCheckout, useOnboardingVariant } from "@/utils/useOnboardingVariant";
import {
	useConfirmPrimingMutation,
	useLazyGetOnboardingPrimingQuery,
	useSubmitPrimingAnswerMutation,
} from "@/pages/Private/redux/onboardingIcp/onboardingIcp.api";

import { PrimingInfo, PrimingQuestion } from "../schema/icp";
import {
	AiMessage,
	ChatChip,
	IcpHighlightList,
	TypingIndicator,
	UserMessage,
} from "../components/chat/ChatPrimitives";
import { ValuePropBox } from "../components/chat/ValuePropBox";

import { DEFAULT_PRIMING_TIMING, OnboardingPrimingTiming, sleep } from "./onboardingChatTiming";

interface ChatEntry {
	id: number;
	kind: "ai" | "user";
	node: ReactNode;
	chips?: ChatChip[];
}

/** Which question the free-text input is currently answering, if any. */
type InputMode = "hidden" | "WHEN" | "WHAT";

/** The drafts are bulleted lists, but an answer is stored as one phrase. */
const toAnswer = (items: string[]): string => items.join(", ");

export type { OnboardingPrimingTiming };

/**
 * Step 5 of the pre-checkout onboarding (PREC-3712): the chat reopens after the
 * Track step and asks three questions before checkout — WHEN (which signals to
 * monitor), WHY (why you, why now) and WHAT (what to research before a call).
 *
 * Each question opens with an AI draft the user can confirm in one click or
 * replace with their own words. Answers are persisted one at a time, so a user
 * who abandons mid-chat resumes at the first unanswered question rather than
 * starting over.
 */
export const OnboardingPrime: FunctionComponent<{ timing?: OnboardingPrimingTiming }> = ({
	timing,
}) => {
	const { draftWaitMs, pollIntervalMs, beatMs } = { ...DEFAULT_PRIMING_TIMING, ...timing };
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, string>) =>
		t(`onboarding.prime.${key}`, options);

	const navigate = useNavigate();

	const skipCheckout = skipsCheckout(useOnboardingVariant());

	const [fetchPriming] = useLazyGetOnboardingPrimingQuery();
	const [submitAnswer] = useSubmitPrimingAnswerMutation();
	const [confirmPriming] = useConfirmPrimingMutation();

	const [messages, setMessages] = useState<ChatEntry[]>([]);
	const [isTyping, setIsTyping] = useState(false);
	const [inputMode, setInputMode] = useState<InputMode>("hidden");
	const [inputValue, setInputValue] = useState("");

	const cancelledRef = useRef(false);
	const bootedRef = useRef(false);
	const idRef = useRef(0);
	const scrollRef = useRef<HTMLDivElement>(null);
	/** The drafts the chat opens each question with. */
	const draftsRef = useRef<PrimingInfo["drafts"] | null>(null);
	/** Live contents of the WHY box — see ValuePropBox on why it is not state. */
	const valuePropRef = useRef("");

	const addAi = (node: ReactNode, chips?: ChatChip[]) => {
		idRef.current += 1;
		setMessages((prev) => [...prev, { id: idRef.current, kind: "ai", node, chips }]);
	};

	const addUser = (text: string) => {
		idRef.current += 1;
		setMessages((prev) => [...prev, { id: idRef.current, kind: "user", node: text }]);
	};

	const clearChips = () => {
		setMessages((prev) => prev.map((entry) => ({ ...entry, chips: undefined })));
	};

	useEffect(() => {
		// Feature-checked: jsdom (tests) has no Element.scrollTo.
		if (scrollRef.current && typeof scrollRef.current.scrollTo === "function") {
			scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
		}
	}, [messages, isTyping]);

	/** Waits for the pre-warmed drafts; returns the last state either way. */
	const pollUntilReady = async (): Promise<PrimingInfo | null> => {
		const deadline = Date.now() + draftWaitMs;
		let latest: PrimingInfo | null = null;

		while (!cancelledRef.current && Date.now() < deadline) {
			try {
				latest = await fetchPriming().unwrap();

				if (latest.status === "READY") {
					return latest;
				}
			} catch (err) {
				console.error(err);
			}

			await sleep(pollIntervalMs);
		}

		return latest;
	};

	/** Persists one answer. Empty text (drafts never arrived) simply stores nothing. */
	const persistAnswer = async (question: PrimingQuestion, answer: string) => {
		if (!answer.trim()) {
			return;
		}

		try {
			await submitAnswer({ question, answer }).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	const askWhen = () => {
		const draft = draftsRef.current?.signals ?? [];

		if (!draft.length) {
			addAi(<>{ts("whenQuestionNoDraft")}</>);
			setInputMode("WHEN");

			return;
		}

		addAi(
			<>
				<span className="block">{ts("whenQuestionPrefix")}</span>
				<IcpHighlightList items={draft} />
				<span className="block">{ts("whenQuestionSuffix")}</span>
			</>,
			[
				{ label: ts("chipLooksGood"), onClick: () => handleConfirmWhen(toAnswer(draft)) },
				{ label: ts("chipChangeSignals"), onClick: () => handleStartEdit("WHEN") },
			]
		);
	};

	const askWhy = () => {
		const draft = draftsRef.current?.valueProp ?? "";

		valuePropRef.current = draft;

		addAi(
			<>
				{ts("whyQuestion")}
				<ValuePropBox
					defaultValue={draft}
					placeholder={ts("whyPlaceholder")}
					onChange={(value) => {
						valuePropRef.current = value;
					}}
				/>
			</>,
			[{ label: ts("chipContinue"), onClick: () => handleConfirmWhy(draft) }]
		);
	};

	const askWhat = () => {
		const draft = draftsRef.current?.research ?? [];

		if (!draft.length) {
			addAi(<>{ts("whatQuestionNoDraft")}</>);
			setInputMode("WHAT");

			return;
		}

		addAi(
			<>
				<span className="block">{ts("whatQuestionPrefix")}</span>
				<IcpHighlightList items={draft} />
				<span className="block">{ts("whatQuestionSuffix")}</span>
			</>,
			[
				{ label: ts("chipLooksGood"), onClick: () => handleConfirmWhat(toAnswer(draft)) },
				{ label: ts("chipChangeResearch"), onClick: () => handleStartEdit("WHAT") },
			]
		);
	};

	/** Beat between the user's turn and the AI's reply. Returns false when unmounted. */
	const aiBeat = async (): Promise<boolean> => {
		setIsTyping(true);
		await sleep(beatMs);

		if (cancelledRef.current) {
			return false;
		}

		setIsTyping(false);

		return true;
	};

	const handleConfirmWhen = async (answer: string) => {
		clearChips();
		addUser(ts("chipLooksGood"));
		await persistAnswer("WHEN", answer);

		if (!(await aiBeat())) {
			return;
		}

		addAi(<>{ts("whenAckConfirmed")}</>);
		askWhy();
	};

	const handleConfirmWhy = async (draft: string) => {
		const answer = valuePropRef.current.trim();

		clearChips();

		if (answer) {
			addUser(answer);
		}

		await persistAnswer("WHY", answer);

		if (!(await aiBeat())) {
			return;
		}

		addAi(<>{answer && answer !== draft ? ts("whyAckEdited") : ts("whyAckConfirmed")}</>);
		askWhat();
	};

	const handleConfirmWhat = async (answer: string) => {
		clearChips();
		addUser(ts("chipLooksGood"));
		await persistAnswer("WHAT", answer);

		if (!(await aiBeat())) {
			return;
		}

		addAi(<>{ts("whatAckConfirmed")}</>);
		finish();
	};

	/** The chip path into the free-text input — the typed answer replaces the draft. */
	const handleStartEdit = (question: InputMode) => {
		clearChips();
		setInputMode(question);
	};

	const handleSendWhen = async (text: string) => {
		addUser(text);
		setInputMode("hidden");
		await persistAnswer("WHEN", text);

		if (!(await aiBeat())) {
			return;
		}

		addAi(<>{ts("whenAckEdited")}</>);
		askWhy();
	};

	const handleSendWhat = async (text: string) => {
		addUser(text);
		setInputMode("hidden");
		await persistAnswer("WHAT", text);

		if (!(await aiBeat())) {
			return;
		}

		addAi(<>{ts("whatAckEdited")}</>);
		finish();
	};

	const handleSend = () => {
		const text = inputValue.trim();

		if (!text) {
			return;
		}

		setInputValue("");

		if (inputMode === "WHEN") {
			handleSendWhen(text);
		} else if (inputMode === "WHAT") {
			handleSendWhat(text);
		}
	};

	const finish = async () => {
		try {
			await confirmPriming().unwrap();
		} catch (err) {
			console.error(err);
		}

		await sleep(beatMs);

		if (!cancelledRef.current) {
			// V2 (PREC-3719) runs the same chat and then finishes — no plan to pick.
			navigate(skipCheckout ? ROUTE_CONFIG.ONBOARDING_DONE : ROUTE_CONFIG.ONBOARDING_SUBSCRIPTION);
		}
	};

	/** Resumes at the first unanswered question — an abandoned chat keeps its answers. */
	const resumeFrom = (info: PrimingInfo) => {
		if (!info.answers.signals) {
			askWhen();
		} else if (!info.answers.valueProp) {
			askWhy();
		} else if (!info.answers.research) {
			askWhat();
		} else {
			finish();
		}
	};

	useEffect(() => {
		if (bootedRef.current) {
			return;
		}

		bootedRef.current = true;
		cancelledRef.current = false;

		const boot = async () => {
			setIsTyping(true);

			const info = await pollUntilReady();

			if (cancelledRef.current) {
				return;
			}

			setIsTyping(false);
			draftsRef.current = info?.drafts ?? null;

			const isFresh = !info?.answers.signals && !info?.answers.valueProp;

			if (isFresh) {
				addAi(<>{ts("opener")}</>);
				await sleep(beatMs);

				if (cancelledRef.current) {
					return;
				}
			}

			if (info) {
				resumeFrom(info);
			} else {
				askWhen();
			}
		};

		boot();

		return () => {
			cancelledRef.current = true;
		};
	}, []);

	return (
		<div className="w-full flex flex-col items-center justify-center">
			<div className="flex h-[680px] max-h-[85vh] w-[560px] max-w-[100%] flex-col rounded-[20px] bg-white shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)]">
				<div className="flex items-center gap-2 border-b border-gray-200 px-6 py-3.5">
					<img alt="logo" className="h-7 w-7" src={AppLogo} />
					<span className="text-sm font-bold">Signalist</span>
				</div>

				<div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5">
					{messages.map((entry) =>
						entry.kind === "ai" ? (
							<AiMessage key={entry.id} chips={entry.chips}>
								{entry.node}
							</AiMessage>
						) : (
							<UserMessage key={entry.id}>{entry.node}</UserMessage>
						)
					)}
					{isTyping && <TypingIndicator />}
				</div>

				{inputMode !== "hidden" && (
					<div className="flex items-center gap-2 border-t border-gray-200 px-4 py-3">
						<input
							autoFocus
							className="input-styles h-10 flex-1 rounded-lg border px-3 text-ssm"
							placeholder={inputMode === "WHEN" ? ts("whenPlaceholder") : ts("whatPlaceholder")}
							value={inputValue}
							onChange={(event) => setInputValue(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Enter") {
									handleSend();
								}
							}}
						/>
						<button
							className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg bg-blue-action text-lg text-white transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50"
							disabled={!inputValue.trim()}
							type="button"
							onClick={handleSend}
						>
							↑
						</button>
					</div>
				)}
			</div>
		</div>
	);
};
